import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Eye, FileText, Sparkles, Trash2, Upload } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi, pedir } from "@/api/cliente"
import { NOMBRE_ARCHIVO, type TipoArchivo } from "@/componentes/personas/lectorPersonal"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type DocumentoPersona = {
  id: number
  tipo: TipoArchivo
  nombre_original: string
  mime: string
  bytes: number
  leido_con_ia: boolean
  creado_en: string
}

/**
 * Los documentos del expediente: cédula, RIF, currículo y lo demás.
 *
 * ⚠ **Fuera del `<form>` de la ficha**, como los contratos: cada subida se guarda sola,
 * y un botón de aquí dentro del formulario lo enviaría.
 *
 * ⚠ **Retirar no borra**: el servidor lo marca y la bitácora dice quién. Es evidencia
 * —con qué cédula se contrató a alguien— y por eso la confirmación lo dice.
 */
export function DocumentosDePersona({ personaId }: { personaId: number }) {
  const cliente = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeEscribir = tienePermiso(usuario, "persona:administrar")
  const [tipo, setTipo] = useState<TipoArchivo>("CEDULA")
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const [retirando, setRetirando] = useState<number | null>(null)
  const clave = ["persona-documentos", personaId]

  const consulta = useQuery({
    queryKey: clave,
    queryFn: () => api.get<DocumentoPersona[]>(`/api/v1/personas/${personaId}/documentos`),
  })

  const subir = useMutation({
    mutationFn: (archivo: File) => {
      const datos = new FormData()
      datos.append("tipo", tipo)
      datos.append("archivo", archivo)
      return pedir(`/api/v1/personas/${personaId}/documentos`, { method: "POST", body: datos })
    },
    onSuccess: () => cliente.invalidateQueries({ queryKey: clave }),
  })

  const retirar = useMutation({
    mutationFn: (id: number) =>
      pedir(`/api/v1/personas/${personaId}/documentos/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRetirando(null)
      cliente.invalidateQueries({ queryKey: clave })
    },
  })

  const error = subir.error ?? retirar.error
  const documentos = consulta.data ?? []

  return (
    <section aria-label="Documentos del expediente">
      <h3 className="rotulo mb-3">Documentos</h3>

      {consulta.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando documentos…
        </p>
      )}
      {consulta.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar los documentos.
        </p>
      )}
      {consulta.isSuccess && documentos.length === 0 && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Sin documentos. Súbelos aquí o léelos con IA desde la ficha.
        </p>
      )}

      {documentos.length > 0 && (
        <ul className="space-y-2">
          {documentos.map((d) => (
            <li key={d.id} className="superficie flex flex-wrap items-center gap-2 p-2 text-sm">
              <FileText size={16} aria-hidden={true} className="shrink-0" />
              <Insignia tono="info">{NOMBRE_ARCHIVO[d.tipo]}</Insignia>
              {d.leido_con_ia && (
                <span title="El tipo lo propuso el lector IA">
                  <Sparkles size={14} aria-label="Leído con IA" />
                </span>
              )}
              <span className="min-w-0 flex-1 truncate">{d.nombre_original}</span>
              <span className="text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                {comoFecha(d.creado_en)}
              </span>
              <button
                type="button"
                aria-label={`Ver ${d.nombre_original}`}
                className="toque-cristal flex h-9 w-9 items-center justify-center rounded"
                onClick={() =>
                  setViendo({
                    ruta: `/api/v1/personas/${personaId}/documentos/${d.id}/archivo`,
                    nombre: d.nombre_original,
                  })
                }
              >
                <Eye size={16} aria-hidden={true} />
              </button>
              {puedeEscribir &&
                (retirando === d.id ? (
                  <span className="flex items-center gap-2 text-xs">
                    ¿Retirarlo? Queda en la bitácora.
                    <Boton tamano="sm" onClick={() => setRetirando(null)}>
                      No
                    </Boton>
                    <Boton
                      tamano="sm"
                      variante="primario"
                      disabled={retirar.isPending}
                      onClick={() => retirar.mutate(d.id)}
                    >
                      Retirar
                    </Boton>
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={`Retirar ${d.nombre_original}`}
                    className="toque-cristal flex h-9 w-9 items-center justify-center rounded"
                    onClick={() => setRetirando(d.id)}
                  >
                    <Trash2 size={16} aria-hidden={true} />
                  </button>
                ))}
            </li>
          ))}
        </ul>
      )}

      {puedeEscribir && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            aria-label="Tipo de documento"
            className="campo w-auto"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoArchivo)}
          >
            {(Object.keys(NOMBRE_ARCHIVO) as TipoArchivo[]).map((t) => (
              <option key={t} value={t}>
                {NOMBRE_ARCHIVO[t]}
              </option>
            ))}
          </select>
          <label className="boton-secundario inline-flex h-9 cursor-pointer items-center gap-2 px-3 text-sm">
            <Upload size={14} aria-hidden={true} />
            {subir.isPending ? "Subiendo…" : "Subir documento"}
            <input
              type="file"
              accept="application/pdf,image/png,image/jpeg,image/webp"
              className="sr-only"
              disabled={subir.isPending}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) subir.mutate(f)
                e.target.value = ""
              }}
            />
          </label>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
          {error instanceof ErrorApi ? error.message : "No se pudo completar"}
        </p>
      )}

      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </section>
  )
}
